import React, { FunctionComponent, memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
    EMPTY_OBJECT_TYPE,
    EMPTY_PROJECT_WORK_OBJECT,
    ObjectType,
    ProjectWorkObject,
    WorkObject
} from '../../../../responses/objects'
import styles from './ObjectPicker.module.sass'
import Tabs from '../../../navigation/Tabs/Tabs'
import { useRootDispatch, useRootSelector, useSubject } from '../../../../utils/hooks'
import { map, switchMap } from 'rxjs/operators'
import { apiRequests } from '../../../../http/config'
import { flow, identity } from 'fp-ts/es6/function'
import * as R from 'fp-ts/es6/Record'
import { keys } from 'fp-ts/es6/Record'
import * as N from 'fp-ts/es6/NonEmptyArray'
import * as A from 'fp-ts/es6/Array'
import { isNonEmpty } from 'fp-ts/es6/Array'
import * as O from 'fp-ts/es6/Option'
import { pipe } from 'fp-ts/function'
import { concat, createIdGenerator, defaultValue, entries, max, objectDeclination, snoc } from '../../../../utils/utils'
import clsx from 'clsx'
import Field from '../../../controls/Field/Field'
import { motion } from 'framer-motion'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import Spacer from '../../../etc/Spacer'
import { objectName, objectType } from '../../../../utils/lenses'
import { entityElem, entityRemove, eqEntity, isNew, isOld } from '../../../../utils/entity-utils'
import { objectActions } from '../../../../redux/data/object/object-actions'
import ObjectPanel from '../../elements/ObjectPanel/ObjectPanel'
import TypeElement from '../../elements/TypeElement/TypeElement'
import EditIcon from 'files/edit.svg'
import CopyIcon from 'files/copy.svg'
import CloseIcon from 'files/close.svg'
import SearchIcon from 'files/search.svg'
import PlusIcon from 'files/plus.svg'
import WithData from '../../../pages/projects/ProjectEdit/WithData'
import { contramap, ordString } from 'fp-ts/es6/Ord'
import { RequestStatus } from '../../../../responses/response'
import { useUpdateEffect } from 'react-use'
import { Colors } from '../../../../styles'
import { ADTType, makeADT, ofType } from '@morphic-ts/adt'


const EditState = makeADT('type')({
    none: ofType<{ type: 'none' }>(),
    update: ofType<{ type: 'update', object: WorkObject, name: string }>()
})

const ALL = 'Все'

const generator = createIdGenerator()

const ordObjectName = contramap((object: WorkObject) => object.name)(ordString)

const nameRegexp = /Копия\s*(\d*)/g

const appendName = (name = ''): string => {
    if (!name) {
        return 'Копия'
    }
    const match = [...name.matchAll(nameRegexp)]
    if (isNonEmpty(match)) {
        const index = (parseInt(match[0][1]) || 1) + 1
        return name.replace(nameRegexp, `Копия ${index}`)
    }
    return name + ' Копия'
}

export const nextObjectName = (name: string, workObject: WorkObject, objects: ProjectWorkObject[]): string => {
    const originalName = name.replace(nameRegexp, '').trim()
    return pipe(
        objects,
        A.filter(object => object.work_object_type.work_object.id == workObject.id),
        A.map(flow(objectName.get, defaultValue(''))),
        A.filter(name => name.includes(originalName)),
        max(ordString),
        O.getOrElse(() => ''),
        appendName,
    )
}

const createObject = (work_object: WorkObject, type: ObjectType, name: string): ProjectWorkObject => ({
    ...EMPTY_PROJECT_WORK_OBJECT,
    name,
    id: generator.next().value,
    work_object_type: {
        ...type,
        work_object,
    },
})

const createCopy = (object: WorkObject, name: string,): ProjectWorkObject =>
    createObject(object, EMPTY_OBJECT_TYPE, name)

interface Props {
    className?: string,
    onToggle: (value: boolean) => void,
    objects: ProjectWorkObject[],
    onChange: (objects: ProjectWorkObject[]) => void,
}

const ObjectPicker: FunctionComponent<Props> = props => {
    const dispatch = useRootDispatch()
    const [search, setSearch] = useState('')
    const [category, setCategory] = useState(ALL)
    const [edited, setEdited] = useState<ProjectWorkObject[]>([])
    const [editState, setEditState] = useState<ADTType<typeof EditState>>(() => EditState.as.none({}))
    const updateStatus = useRootSelector(state => state.data.project.updateStatus)

    const [initialObjects, setInitialObjects] = useState(props.objects)
    const [objects$, objects] = useSubject<string, WorkObject[]>([], input$ => input$.pipe(
        switchMap(search => apiRequests.dictionary.list({params: {key: 'work-object'}, query: {limit: -1, search}})),
        map(res => res.response.success ? res.response.rows : []),
    ))

    const allObjects = useMemo(() => edited.concat(initialObjects.filter(isOld)), [edited, initialObjects])
    const objectsByCategory = useMemo(() =>
            N.groupBy((object: WorkObject) => object.category.name)(objects),
        [objects]
    )
    const grouped = useMemo(() => pipe(
        allObjects,
        N.groupBy((object: ProjectWorkObject) => object.work_object_type.work_object.id as string),
        R.map(N.groupBy((object: ProjectWorkObject) => object.name ?? '')),
    ), [allObjects])
    const selected = useMemo(() =>
            category === ALL ? A.sort(ordObjectName)(objects) : objectsByCategory[category] ?? [],
        [category, objectsByCategory, objects]
    )
    const createdObjects = edited.filter(flow(objectType.get, isOld))

    useEffect(() => {
        if (!keys(objectsByCategory).includes(category)) {
            setCategory(ALL)
        }
    }, [objectsByCategory, category])
    useEffect(() => objects$.next(search), [objects$, search])

    useUpdateEffect(() => {
        setEdited(initialObjects.filter(isNew))
    }, [initialObjects])

    const rename = useCallback((workObject: WorkObject, name: string, newName: string) => {
        setInitialObjects(A.map((object: ProjectWorkObject) => {
            if (name === (object.name ?? '') && object.work_object_type.work_object.id === workObject.id) {
                dispatch(objectActions.update.request({
                    objectId: object.id,
                    changes: {name: newName}
                }))
                return objectName.set(newName)(object)
            }
            return object
        }))
        setEdited(A.map((object: ProjectWorkObject) =>
            (name === (object.name ?? '') && object.work_object_type.work_object.id === workObject.id) ?
                objectName.set(newName)(object) :
                identity(object)
        ))
    }, [dispatch])

    return <motion.div className={styles.container}>
        <div className={styles.header}>
            <div className={styles.field}>
                <SearchIcon/>
                <Field
                    placeholder={'Поиск'}
                    value={search}
                    onChange={setSearch}
                />
                <Spacer/>
                <Button onClick={() => props.onToggle(false)} title={'Отмена'}>
                    <CloseIcon/>
                </Button>
                {
                    createdObjects.length > 0 && <Button
                        className={styles.ok}
                        disabled={updateStatus === RequestStatus.FETCHING}
                        color={Colors.blue}
                        justify={'space-between'}
                        buttonStyle={ButtonStyle.BORDERED}
                        onClick={() => props.onChange(createdObjects)}
                    >
                        <PlusIcon/>
                        {objectDeclination(createdObjects.length)}
                        <span/>
                    </Button>
                }
            </div>
            <Tabs
                className={styles.tabs}
                items={[ALL].concat(keys(objectsByCategory))}
                selected={category}
                renderer={tab => <div>
                    {
                        allObjects.some(object => object.work_object_type.work_object.category.name === tab) &&
                        <div className={styles.dot}/>
                    }
                    {tab}
                </div>}
                itemToKey={identity}
                onChange={setCategory}
            />
        </div>

        <table>
            {
                selected.map(object => <tbody key={object.id}>
                {
                    pipe(
                        R.lookup(object.id as string, grouped),
                        O.map(entries),
                        O.getOrElse<[string, ProjectWorkObject[]][]>(() => [['', []]]),
                        A.map(([name, objects]) => {

                            const X = object.types.length > 1 ? object.types.slice(0,1): object.types
                            const exist = isNonEmpty(A.intersection(eqEntity)(objects, initialObjects.filter(isOld)))
                            const created = isNonEmpty(A.intersection(eqEntity)(objects.map(objectType.get), X))
                            const edit = EditState.is.update(editState) && editState.object.id == object.id && editState.name === name
                            const byType = N.groupBy<ProjectWorkObject>(object => object.work_object_type.id as string)(objects)
                            const isEmpty = A.isEmpty(objects)
                            return <tr key={name} className={clsx({
                                [styles.edit]: edit,
                                [styles.exist]: exist,
                                [styles.created]: created,
                            })}>
                                <td>
                                    <div className={styles.types}>
                                        {
                                            X.map(type => {
                                                const project_work_object = pipe(
                                                    byType,
                                                    R.lookup(type.id as string),
                                                    O.map(N.head),
                                                    O.getOrElse(() => EMPTY_PROJECT_WORK_OBJECT)
                                                )
                                                return <TypeElement
                                                    key={type.id}
                                                    type={type}
                                                    checked={entityElem(project_work_object, objects)}
                                                    disabled={entityElem(project_work_object, initialObjects.filter(isOld))}
                                                    onClick={checked => setEdited(checked ?
                                                        A.cons(createObject(object, type, name)) :
                                                        entityRemove(project_work_object)
                                                    )}
                                                />
                                            })
                                        }
                                    </div>
                                </td>
                                <td><ObjectPanel object={object}>
                                    {
                                        !edit && name && <div className={styles.name}>[{name}]</div>
                                    }
                                    {
                                        <div className={styles.controls}>
                                            {
                                                edit ? <WithData initialData={name}>
                                                    {
                                                        ({data, setData}) => <Field
                                                            autoFocus={true}
                                                            value={data}
                                                            onBlur={() => setEditState(EditState.as.none({}))}
                                                            onEscape={() => setEditState(EditState.as.none({}))}
                                                            onEnter={() => {
                                                                if (isEmpty) {
                                                                    setEdited(snoc(createCopy(object, data)))
                                                                } else {
                                                                    rename(object, name, data)
                                                                }
                                                                setEditState(EditState.as.none({}))
                                                            }}
                                                            onChange={setData}
                                                        />
                                                    }
                                                </WithData> : <Button
                                                    title={'Переименовать'}
                                                    onClick={() => setEditState(EditState.as.update({
                                                        object,
                                                        name
                                                    }))}
                                                >
                                                    <EditIcon/>
                                                </Button>
                                            }
                                            <Button
                                                title={'Дублировать'}
                                                onClick={() => {
                                                    setEdited(concat(
                                                        isEmpty ? [createCopy(object, '')] : [],
                                                        [createCopy(object, nextObjectName(name, object, allObjects))]
                                                    ))
                                                }}
                                            >
                                                <CopyIcon/>
                                            </Button>
                                        </div>
                                    }
                                </ObjectPanel></td>
                            </tr>
                        })
                    )
                }
                </tbody>)
            }
        </table>
    </motion.div>
}

export default memo(ObjectPicker)